import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'
import { LIVE, live } from './live'
import { keys } from './queries'
import {
  applyPreferenceChange,
  normalizeNotification,
  parseUnreadCount,
  type AppNotification,
  type NotificationPreferences,
  type PreferencePayload,
  type RawNotification,
} from './notifications'
import type { Paginated } from '@/types/api'

/*
 * The signed-in person's notifications and what they choose to receive.
 *
 * Everything under `keys.notifications` is invalidated together — by the
 * realtime `.personal.update` push (hooks/useRealtime.ts) and by every
 * mutation here — so the badge, the bell's list and the notification centre
 * cannot disagree for longer than one request.
 *
 * The preferences are deliberately *not* under that key. They change only when
 * the person presses a switch, and putting them there would refetch them on
 * every new notification for nothing.
 */

/** The page size the API pages the list at. Sent so an older API agrees. */
const PAGE_SIZE = 25

export interface NotificationListFilters {
  /** Comma-separated type keys; absent for every type. See `typeParam`. */
  type?: string
  unread?: boolean
}

type NotificationPage = Omit<Paginated<RawNotification>, 'data'> & { data: AppNotification[] }

async function fetchPage(page: number, filters: NotificationListFilters): Promise<NotificationPage> {
  const params: Record<string, string | number> = { page, perPage: PAGE_SIZE }

  if (filters.type) params.type = filters.type

  if (filters.unread) {
    params.unread = 1
    // What the API called it before the notification centre existed. Sent as
    // well, so the filter holds against either version of the server.
    params.unreadOnly = 1
  }

  const reply = (await api.get<Paginated<RawNotification>>('/notifications', { params })).data

  return { ...reply, data: reply.data.map(normalizeNotification) }
}

/**
 * The bell's list: the first page, newest first.
 *
 * `enabled` is the panel being open. The badge has its own small query; the
 * list behind it is only worth fetching — and polling — while somebody is
 * looking at it.
 */
export function useRecentNotifications(enabled: boolean) {
  return useQuery({
    queryKey: [...keys.notifications, 'recent'],
    queryFn: () => fetchPage(1, {}),
    enabled,
    ...live(LIVE.operational),
  })
}

/**
 * The notification centre's list, a page at a time.
 *
 * Infinite rather than numbered pages because the list is grouped by day: a
 * page boundary through the middle of Tuesday would show Tuesday twice, once
 * at the foot of one page and again at the top of the next.
 */
export function useNotificationList(filters: NotificationListFilters) {
  return useInfiniteQuery({
    queryKey: [...keys.notifications, 'list', filters],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchPage(pageParam, filters),
    getNextPageParam: (last) =>
      last.meta.current_page < last.meta.last_page ? last.meta.current_page + 1 : undefined,
    ...live(LIVE.operational),
  })
}

/**
 * Drives the badge.
 *
 * Polled as well as pushed: the realtime socket is optional per deployment and
 * a phone that was asleep was not listening, so the poll is the floor and the
 * push the improvement. Paused while the tab is hidden so a forgotten tab does
 * not poll all night.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: keys.unreadCount,
    queryFn: async () => parseUnreadCount((await api.get('/notifications/unread-count')).data),
    ...live(LIVE.operational),
  })
}

function useInvalidateNotifications() {
  const qc = useQueryClient()

  // `keys.unreadCount` starts with `keys.notifications`, so one call covers the
  // badge, the bell and the centre.
  return () => qc.invalidateQueries({ queryKey: keys.notifications })
}

export function useMarkNotificationRead() {
  const invalidate = useInvalidateNotifications()

  return useMutation({
    mutationFn: async (id: string) => (await api.post(`/notifications/${id}/read`)).data,
    onSuccess: invalidate,
  })
}

export function useMarkAllNotificationsRead() {
  const invalidate = useInvalidateNotifications()

  return useMutation({
    mutationFn: async () =>
      (await api.post<{ message?: string }>('/notifications/read-all')).data,
    onSuccess: invalidate,
  })
}

export function useDeleteNotification() {
  const invalidate = useInvalidateNotifications()

  return useMutation({
    mutationFn: async (id: string) =>
      (await api.delete<{ message?: string }>(`/notifications/${id}`)).data,
    onSuccess: invalidate,
  })
}

// ---------------------------------------------------------------------------
// What I get, and how
// ---------------------------------------------------------------------------

const PREFERENCES_KEY = ['notification-preferences'] as const
const SAVE_PREFERENCES_KEY = [...PREFERENCES_KEY, 'save'] as const

export function useNotificationPreferences() {
  return useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () =>
      (await api.get<{ data: NotificationPreferences }>('/me/notification-preferences')).data.data,
  })
}

/**
 * Saves one switch press.
 *
 * Optimistic, because a switch is a promise that the change is already live —
 * one that sits still for a round trip on the plant wifi and then jumps reads
 * as broken. A failure puts the old state back.
 *
 * The server's reply replaces the copy only when no other press is still on
 * its way. Two quick presses on different rows each answer with the whole
 * list, and the first answer arriving would otherwise undo the second press on
 * screen until the second answer caught up.
 */
export function useSaveNotificationPreferences() {
  const qc = useQueryClient()

  return useMutation({
    mutationKey: SAVE_PREFERENCES_KEY,
    mutationFn: async (types: PreferencePayload) =>
      (
        await api.put<{ data: NotificationPreferences }>('/me/notification-preferences', {
          types,
        })
      ).data.data,
    onMutate: async (types) => {
      await qc.cancelQueries({ queryKey: PREFERENCES_KEY })

      const previous = qc.getQueryData<NotificationPreferences>(PREFERENCES_KEY)

      if (previous) {
        qc.setQueryData(PREFERENCES_KEY, applyPreferenceChange(previous, types))
      }

      return { previous }
    },
    onError: (_error, _types, context) => {
      if (context?.previous) qc.setQueryData(PREFERENCES_KEY, context.previous)
      // And then the truth, in case another press was in flight when the
      // snapshot above was taken.
      qc.invalidateQueries({ queryKey: PREFERENCES_KEY })
    },
    onSuccess: (saved) => {
      // This mutation still counts as pending while its own callbacks run.
      if (qc.isMutating({ mutationKey: SAVE_PREFERENCES_KEY }) <= 1) {
        qc.setQueryData(PREFERENCES_KEY, saved)
      }
    },
  })
}
